import { useState, useEffect, useCallback } from "react"
import { useStaticQuery, graphql, navigate } from "gatsby"
import { SearchComponents as Components } from "../providers/search"

import { useApp } from "./useApp"
import { useRoutes } from "./useRoutes"

export const useSearch = location => {
  const {
    config: {
      settings: { routes },
    },
    searchTerms,
    setSearchTerms,
    globalStateReducer,
  } = useApp()
  const { getUrlParameter } = useRoutes()
  const [, dispatch] = globalStateReducer
  const [query, setQuery] = useState(``)
  const [timer, setTimer] = useState()

  const debounce = 500

  const handleChange = (value, triggerQuery) => {
    setQuery(value)
    // Set a timer for debouncing, if it's passed, call triggerQuery.
    setTimer(
      setTimeout(() => {
        triggerQuery()
      }, debounce)
    )
  }

  const handleKey = e => {
    if (e.key === "Enter") {
      clearTimeout(timer)
      if (query.length >= 3) {
        processSearch()
      }
    }
  }

  const { search: settings } = useStaticQuery(graphql`
    query SEARCH_SETTINGS {
      search: allThirdPartySearch {
        edges {
          node {
            ...GatsbySearchFragment
          }
        }
      }
    }
  `)

  const { filters, sort, fields, redirects, ...rest } =
    settings?.edges?.map(({ node }) => ({
      ...node,
    }))[0] || false

  const curations = rest.curations ? rest.curations : null

  useEffect(() => {
    const defaultQuery = getUrlParameter(`q`, location)
    if (defaultQuery) {
      defaultQuery && defaultQuery !== searchTerms && setSearchTerms(defaultQuery)
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getFilter = filterId => filters.find(({ id }) => id === filterId)

  const getFilterReact = (filterId, exclude) =>
    getFilter(filterId)
      ?.options?.filter(({ id }) => !exclude || exclude !== id)
      ?.map(({ id }) => id) || []

  const getFilterListDefaults = ({ showSearch }) => ({
    showSearch: showSearch || false,
  })

  const getFilterRangeDefaults = () => ({
    data: [
      { start: "0", label: "All prices" },
      { start: "0", end: "149", label: "$0 - $149" },
      { start: "150", end: "249", label: "$150 - $249" },
      { start: "250", end: "349", label: "$250 - $349" },
      { start: "350", label: "$350+" },
    ],
    showRadio: false,
  })

  const getFilterSliderDefaults = ({ interval, step }) => ({
    stepValue: interval || 50,
    interval: step || 10,
    defaultValue: (min, max) => ({ start: min, end: Math.min(2000, max) }),
    rangeLabels: (min, max) => ({ start: `$${min}`, end: `$${max}` }),
    showHistogram: false,
  })

  const getSearchFormConfig = () => ({
    autosuggest: false,
    dataField: fields?.map(item => item?.field),
    // placeholder: `Search the store...`,
    placeholder: `Search...`,
    fieldWeights: fields?.map(item => item?.importance),
    filterLabel: `Term`,
    size: 0,
    fuzziness: 0,
    highlight: false,
    highlightField: `title`,
    onKeyPress: handleKey,
    onChange: handleChange,
    queryFormat: `and`,
    render: () => null,
    showVoiceSearch: false,
    showIcon: false,
    showClear: true,
    URLParams: false,
    value: query,
  })

  const getSuggestionConfig = () => ({
    componentId: `suggestions`,
    dataField: `title`,
    loader: () => null,
    pagination: false,
    react: {
      and: [`MobileSearchForm`, `DesktopSearchForm`, `PublishedSensor`],
    },
    scrollOnChange: true,
    showResultStats: false,
    size: 2,
    URLParams: false,
    showLoader: false,
  })

  const processSearch = () => {
    if (query.length >= 1) {
      const redirect = redirects?.find(item => item?.query?.toLowerCase() === query.toLowerCase())
      const url = redirect?.url ? redirect?.url : `${routes.SEARCH}?q=${query}`
      setSearchTerms(query)
      dispatch({
        type: "setActiveSearch",
        payload: false,
      })
      navigate(url)
      if (redirect?.url) {
        window.dispatchEvent(new Event(`refreshSearch`))
      }
    }
  }

  const getResultsConfig = ({ filterId }) => {
    return {
      componentId: `page`,
      dataField: `title`,
      size: 9,
      includeFields: ["presentment_price_ranges", "tags", "images", "id", "handle", "title", "url", "variants"],
      react: {
        and: [...getFilterReact(filterId), `CollectionSensor`, `PublishedSensor`, `SearchSensor`, `MobileSortSensor`, `DesktopSortSensor`],
      },
      pagination: false, //getFilter(filterId)?.paginationType !== `infinite_scroll`,
      infiniteScroll: false, //getFilter(filterId)?.paginationType === `infinite_scroll`,
      showResultStats: false,
      scrollOnChange: false,
      URLParams: false,
    }
  }

  const getFiltersConfig = ({ filterId }) => {
    return getFilter(filterId)?.options?.map(option => ({
      id: option?.id,
      title: option?.name,
      type: option?.displayType,
      view: option?.displayView,
      field: option?.field,
      config: {
        componentId: option?.id,
        dataField: option?.field,
        filterLabel: option?.name,
        react: {
          and: [...getFilterReact(filterId, option?.id), `CollectionSensor`, `PublishedSensor`, `SearchSensor`],
        },
        showFilter: option?.settingsShowFilter || true,
        showLoadMore: option?.settingsShowMore || false,
        size: parseInt(option?.displaySize) || 100,
        ...(option?.displayType === `multi` && {
          ...getFilterListDefaults({ showSearch: option?.settingsShowSearch }),
        }),
        ...(option?.displayType === `single` && {
          ...getFilterListDefaults({ showSearch: option?.settingsShowSearch }),
        }),
        ...(option?.displayType === `range` && { ...getFilterRangeDefaults() }),
        ...(option?.displayType === `slider` && {
          ...getFilterSliderDefaults({
            interval: option?.displaySliderInterval,
            step: option?.displaySliderStep,
          }),
        }),
        URLParams: false,
      },
      settings: {
        collapsedDesktop: option?.settingsCollapsedDesktop,
        collapsedMobile: option?.settingsCollapsedMobile,
        uppercaseValues: option?.settingsUppercase,
      },
    }))
  }

  const getSortConfig = type =>
    sort
      ?.filter(({ visibility }) => visibility === "all" || visibility === type)
      .sort((a, b) => a.position - b.position)
      .map(item => ({
        label: item?.name,
        dataField: item?.field,
        sortBy: item?.direction,
      }))

  const getSortQuery = ({ handle, search, sortedBy, terms = searchTerms }) => {
    const sortQuery = []
    const type = terms ? "search" : "collection"
    const curation = resolveCuration(type, type === "search" ? terms : handle)

    if (search) {
      if (sortedBy?.dataField === "_score" && terms) {
        sortQuery.push({
          "curations.position": {
            unmapped_type: "long",
            order: "asc",
            nested: {
              path: "curations",
              filter: {
                term: {
                  "curations.searchTerm.keyword": terms,
                },
              },
            },
          },
        })
      }
    } else if (handle) {
      if (sortedBy?.dataField === "collections.position") {
        sortQuery.push({
          "curations.position": {
            unmapped_type: "long",
            order: "asc",
            nested: {
              path: "curations",
              filter: {
                term: {
                  "curations.collectionHandle.keyword": handle,
                },
              },
            },
          },
        })
      }
    }

    sortQuery.push({ [sortedBy?.dataField]: sortedBy?.sortBy })
    if (curation && curation.boosting) {
      if (0 < curation.boosting.groupings.length) {
        const groupings = curation.boosting.groupings.sort((a, b) => (a.position > b.position ? 1 : -1))

        for (const grouping of groupings) {
          try {
            const query = JSON.parse(grouping.query)
            //eslint-disable-next-line no-extra-boolean-cast
            if (!!query) sortQuery.push(query)
          } catch {
            console.error(`query could not be parsed for boost grouping`, grouping)
          }
        }
      }

      if (0 < curation.boosting.sortings.length) {
        const sortings = curation.boosting.sortings.sort((a, b) => (a.position > b.position ? 1 : -1))

        for (const sorting of sortings) {
          try {
            const query = JSON.parse(sorting.query)
            //eslint-disable-next-line no-extra-boolean-cast
            if (!!query) sortQuery.push(query)
          } catch {
            console.error(`query could not be parsed for boost sorting`, sorting)
          }
        }
      }
    }

    if ("collection" === type) sortQuery.push("_doc")
    else sortQuery.push("_score")
    return { sortQuery }
  }

  const getQueryString = () => {
    return query
  }

  const resolveCuration = useCallback(
    (type, handleOrSearchTerm) => {
      const curation = curations
        ? curations.find(curation => {
            if ("search" === type) return "search" === curation.type && handleOrSearchTerm === curation.searchTerm
            if ("collection" === type) return "collection" === curation.type && handleOrSearchTerm === curation.collectionHandle
            return false
          })
        : null

      return curation ? curation : null
    },
    [curations]
  )

  return {
    getQueryString,
    getSortConfig,
    getSortQuery,
    getSearchFormConfig,
    getSuggestionConfig,
    getResultsConfig,
    getFiltersConfig,
    processSearch,
  }
}

export const SearchComponents = Components
